import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { studentPortalAPI, bankAccountsAPI, linePayAPI } from '../services/api';
import Spinner from '../components/Spinner';
import toast from 'react-hot-toast';

export default function StudentDashboardPage() {
    const [classes, setClasses] = useState([]);
    const [attendance, setAttendance] = useState([]);
    const [notes, setNotes] = useState([]);
    const [bankAccounts, setBankAccounts] = useState([]);
    const [linePayInfo, setLinePayInfo] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [paying, setPaying] = useState(false);
    const [customAmount, setCustomAmount] = useState('');
    const [acknowledging, setAcknowledging] = useState(null);

    useEffect(() => {
        setIsLoading(true);
        Promise.all([
            studentPortalAPI.getMyClasses(),
            studentPortalAPI.getMyAttendance(),
            studentPortalAPI.getMyNotes().catch(() => []),
            bankAccountsAPI.getAll().catch(() => []),
            linePayAPI.getInfo().catch(() => null)
        ]).then(([classesData, attendanceData, notesData, bankData, linePayData]) => {
            setClasses(classesData);
            setAttendance(attendanceData);
            setNotes(notesData || []);
            setBankAccounts(bankData || []);
            setLinePayInfo(linePayData);

            // Check if redirected back from LINE Pay authentication
            const params = new URLSearchParams(window.location.search);
            const linePayStatus = params.get('linepay');
            const transactionId = params.get('transactionId') || sessionStorage.getItem('irie_linepay_tx_id');

            if (linePayStatus === 'confirm' && transactionId) {
                toast.loading('Confirming payment with LINE Pay...', { id: 'linepay-confirm' });
                linePayAPI.confirmPayment({ transactionId })
                    .then(() => {
                        toast.success('🎉 LINE Pay payment confirmed and settled successfully!', { id: 'linepay-confirm' });
                        sessionStorage.removeItem('irie_linepay_tx_id');
                    })
                    .catch((err) => {
                        toast.error(err.response?.data?.error || 'Could not verify LINE Pay transaction.', { id: 'linepay-confirm' });
                    })
                    .finally(() => {
                        window.history.replaceState({}, document.title, window.location.pathname);
                    });
            } else if (linePayStatus === 'cancel') {
                toast('LINE Pay payment was cancelled.', { icon: 'ℹ️' });
                window.history.replaceState({}, document.title, window.location.pathname);
            }
        }).catch(() => {
            toast.error("Could not load your dashboard data.");
        }).finally(() => {
            setIsLoading(false);
        });
    }, []);

    const handlePayOnline = async (amountToPay) => {
        const amt = Math.round(parseFloat(amountToPay));
        if (!amt || amt <= 0) {
            toast.error('Please enter a valid tuition amount.');
            return;
        }

        setPaying(true);
        try {
            const res = await linePayAPI.requestPayment({ amount: amt });
            if (res.transactionId) {
                sessionStorage.setItem('irie_linepay_tx_id', res.transactionId);
            }
            const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
            const targetUrl = (isMobile && res.paymentUrl?.app) ? res.paymentUrl.app : res.paymentUrl?.web;
            if (targetUrl) {
                window.location.href = targetUrl;
            } else {
                toast.error('Failed to get checkout URL from LINE Pay.');
            }
        } catch (err) {
            toast.error(err.response?.data?.error || 'Could not start LINE Pay online payment.');
        } finally {
            setPaying(false);
        }
    };

    const handleAcknowledgeNote = async (noteId) => {
        setAcknowledging(noteId);
        try {
            const { acknowledged_at } = await studentPortalAPI.acknowledgeNote(noteId);
            setNotes(prev => prev.map(n => n.id === noteId ? { ...n, acknowledged_at } : n));
        } catch (err) {
            toast.error('Could not acknowledge note.');
        } finally {
            setAcknowledging(null);
        }
    };

    if (isLoading) {
        return <div className="p-10"><Spinner /></div>;
    }

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                        <span>📚</span> My Upcoming Classes
                    </h2>
                    <div className="bg-white dark:bg-gray-800 p-5 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 space-y-3 min-h-[160px]">
                        {classes.length > 0 ? classes.map(cls => (
                            <div key={cls.id} className="p-3.5 border border-gray-100 dark:border-gray-700 bg-gray-50/70 rounded-lg hover:bg-gray-100/80 transition-colors">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <p className="font-bold text-base text-gray-900 dark:text-gray-100">{cls.name}</p>
                                    {cls.is_one_off && (
                                        <span className="text-[10px] font-bold bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded-full">
                                            📅 Make-up
                                        </span>
                                    )}
                                </div>
                                <p className="text-sm text-gray-600 dark:text-gray-400 font-medium mt-0.5">
                                    {cls.is_one_off && cls.one_off_date
                                        ? new Date(cls.one_off_date).toLocaleDateString()
                                        : cls.day} at {cls.start_time}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Teacher: {cls.teacher_name}</p>
                            </div>
                        )) : <p className="text-sm text-gray-500 dark:text-gray-400 italic py-4">You are not currently enrolled in any classes.</p>}
                    </div>
                </div>

                <div>
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                            <span>📋</span> Recent Attendance
                        </h2>
                        <Link to="/student/attendance" className="text-xs font-semibold text-blue-600 hover:underline">
                            View full history →
                        </Link>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-5 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 min-h-[160px]">
                        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                            {attendance.length > 0 ? attendance.map((record, index) => (
                                <li key={index} className="flex justify-between items-center py-2.5">
                                    <div>
                                        <p className="font-semibold text-sm text-gray-800 dark:text-gray-200">{record.class_name}</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">{new Date(record.attendance_date).toLocaleDateString()}</p>
                                    </div>
                                    <span className={`text-xs px-2.5 py-0.5 rounded-full font-semibold ${
                                        record.status === 'present' ? 'bg-green-100 text-green-800' :
                                        record.status === 'absent' ? 'bg-red-100 text-red-800' :
                                        'bg-amber-100 text-amber-800'
                                    }`}>
                                        {record.status}
                                    </span>
                                </li>
                            )) : <p className="text-sm text-gray-500 dark:text-gray-400 italic py-4">No recent attendance records found.</p>}
                        </ul>
                    </div>
                </div>
            </div>

            {/* Notes from Your Teacher */}
            {notes.length > 0 && (
                <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                        <span>💬</span> Notes from Your Teacher
                    </h2>
                    <div className="bg-white dark:bg-gray-800 p-5 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 space-y-3">
                        {notes.map(note => (
                            <div key={note.id} className="p-3.5 border border-gray-100 dark:border-gray-700 bg-gray-50/70 rounded-lg">
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <p className="text-sm text-gray-800 dark:text-gray-200">"{note.note}"</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                            {note.teacher_name} • {new Date(note.created_at).toLocaleDateString()}
                                        </p>
                                    </div>
                                    {note.acknowledged_at ? (
                                        <span className="text-[11px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full whitespace-nowrap">✓ Seen</span>
                                    ) : (
                                        <button
                                            type="button"
                                            disabled={acknowledging === note.id}
                                            onClick={() => handleAcknowledgeNote(note.id)}
                                            className="text-xs font-semibold text-blue-600 hover:text-blue-800 bg-blue-50 px-2.5 py-1 rounded-lg whitespace-nowrap"
                                        >
                                            Mark as Seen
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Tuition & Payment Options */}
            {(bankAccounts.length > 0 || (linePayInfo && linePayInfo.enabled)) && (
                <div>
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                            <span>💳</span> Tuition & Payment Options (學費繳款方式)
                        </h2>
                        <Link to="/student/payments" className="text-xs font-semibold text-blue-600 hover:underline">
                            Payment history →
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* LINE Pay Card */}
                        {linePayInfo && linePayInfo.enabled && (
                            <div className="bg-white dark:bg-gray-800 p-5 rounded-lg shadow border-2 border-emerald-500/40 relative flex flex-col justify-between">
                                <div>
                                    <div className="flex justify-between items-start mb-2">
                                        <div>
                                            <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wider block">LINE Pay & iPASS MONEY</span>
                                            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
                                                <span>🟢</span> LINE Pay 掃碼付款
                                            </h3>
                                        </div>
                                        <span className="bg-emerald-100 text-emerald-800 text-xs px-2.5 py-0.5 rounded-full font-semibold">
                                            Instant / 即時
                                        </span>
                                    </div>

                                    {linePayInfo.qr_image_url ? (
                                        <div className="bg-gray-50 dark:bg-gray-800/60 p-3 rounded-lg border text-center my-3">
                                            <img
                                                src={linePayInfo.qr_image_url}
                                                alt="LINE Pay QR Code"
                                                className="max-h-48 max-w-full mx-auto rounded shadow-sm object-contain"
                                            />
                                            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-2">
                                                請開啟 LINE App 掃描此行動條碼完成付款
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="bg-emerald-50/50 p-4 rounded border my-3 text-center text-xs text-gray-600 dark:text-gray-400">
                                            📱 支援使用 LINE Pay 轉帳付款至：<strong className="text-gray-800 dark:text-gray-200">{linePayInfo.account_name}</strong>
                                        </div>
                                    )}

                                    <div className="text-xs text-gray-700 dark:text-gray-300 space-y-1 mt-2">
                                        <div>收款人 / 戶名：<span className="font-semibold text-gray-900 dark:text-gray-100">{linePayInfo.account_name}</span></div>
                                        {linePayInfo.account_id && (
                                            <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800/60 p-1.5 rounded border">
                                                <span>LINE ID / 手機：<strong>{linePayInfo.account_id}</strong></span>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(linePayInfo.account_id);
                                                        toast.success('LINE ID copied!');
                                                    }}
                                                    className="bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] px-2 py-0.5 rounded"
                                                >
                                                    Copy
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {/* Online LINE Pay Checkout (Active when merchant credentials are set) */}
                                    {linePayInfo.is_online_ready && (
                                        <div className="mt-4 pt-3 border-t">
                                            <div className="flex items-center justify-between bg-emerald-50 p-2.5 rounded-lg border border-emerald-200 mb-2.5">
                                                <div>
                                                    <div className="text-xs font-bold text-emerald-950 flex items-center gap-1">
                                                        <span>🟢</span> Online LINE Pay (線上即時付款)
                                                    </div>
                                                    <div className="text-[11px] text-emerald-700">
                                                        One-tap checkout with LINE Pay or credit card
                                                    </div>
                                                </div>
                                                {linePayInfo.is_sandbox && (
                                                    <span className="text-[10px] bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded font-bold uppercase tracking-wider">
                                                        Sandbox Mode
                                                    </span>
                                                )}
                                            </div>

                                            <div className="flex gap-2 items-center">
                                                <div className="relative w-36">
                                                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-500 dark:text-gray-400 font-medium">NT$</span>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        placeholder="650"
                                                        value={customAmount}
                                                        onChange={(e) => setCustomAmount(e.target.value)}
                                                        className="w-full border rounded pl-9 pr-2 py-1.5 text-xs focus:ring-1 focus:ring-emerald-500 font-semibold text-gray-800 dark:text-gray-200"
                                                    />
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => handlePayOnline(customAmount || 650)}
                                                    disabled={paying}
                                                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold py-1.5 px-3 rounded text-xs shadow-sm flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
                                                >
                                                    <span>{paying ? 'Connecting to LINE...' : `Pay NT$${customAmount || 650} with LINE Pay`}</span>
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {linePayInfo.instructions && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 italic mt-3 pt-2 border-t">
                                        📝 {linePayInfo.instructions}
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Bank Transfer Accounts */}
                        {bankAccounts.map(acc => (
                            <div key={acc.id} className="bg-white dark:bg-gray-800 p-5 rounded-lg shadow border border-gray-200 dark:border-gray-700 flex flex-col justify-between">
                                <div>
                                    <div className="flex justify-between items-start mb-2">
                                        <div>
                                            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider block">Bank Transfer Account</span>
                                            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">{acc.bank_name} ({acc.bank_code})</h3>
                                        </div>
                                        {acc.is_primary && (
                                            <span className="bg-blue-100 text-blue-800 text-xs px-2.5 py-0.5 rounded-full font-semibold">
                                                Primary Account
                                            </span>
                                        )}
                                    </div>

                                    <div className="bg-gray-50 dark:bg-gray-800/60 p-3 rounded border my-3">
                                        <div className="flex justify-between items-center">
                                            <div>
                                                <div className="text-xs text-gray-500 dark:text-gray-400">Account Number (帳號)</div>
                                                <div className="text-xl font-mono font-bold text-gray-900 dark:text-gray-100 tracking-wider">
                                                    {acc.account_number}
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(acc.account_number);
                                                    toast.success('Account number copied to clipboard!');
                                                }}
                                                className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1.5 rounded font-medium shadow-sm transition-colors"
                                            >
                                                Copy
                                            </button>
                                        </div>
                                        <div className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                                            Beneficiary Name (戶名): <span className="font-semibold text-gray-800 dark:text-gray-200">{acc.account_name}</span>
                                            {acc.branch_name && ` • Branch: ${acc.branch_name}`}
                                        </div>
                                    </div>
                                </div>

                                {acc.instructions && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 italic mt-3 pt-2 border-t">
                                        📝 {acc.instructions}
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}